/**
 * Branding Editor Page
 *
 * Dedicated visual branding editor with live preview.
 * Left panel: controls (colors, fonts, logo, theme mode, custom CSS)
 * Right panel: live preview card that updates in real-time
 *
 * Route: /studio/:verticalKey/branding
 */

import type { BrandingColors, BrandingConfig, ThemeBrandingConfig } from "@tatvex/config";
import {
  ChevronRight,
  Download,
  Home,
  Loader2,
  Moon,
  Palette,
  RefreshCw,
  Save,
  Sun,
  Upload,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

import { apiRequest } from "../api/client";
import { BrandingPresetSelector } from "../components/branding/BrandingPresetSelector";
import { LivePreviewCard } from "../components/branding/LivePreviewCard";
import { exportBrandingJson, importBrandingJson } from "../components/branding/branding-io";
import type { BrandingPreset } from "../components/branding/branding-presets";
import { ColorPicker } from "../components/form-builder/ColorPicker";
import { createConfigApiClient } from "../hooks/use-config-api";
import { useDraftManagement, createLockApiClient } from "../hooks/use-draft-management";
import { useAuthStore } from "../store/authStore";

// ============================================================================
// Types
// ============================================================================

type ThemeMode = "light" | "dark" | "system";

interface ConfigContent {
  readonly content: string;
  readonly version: number;
  readonly status: "draft" | "live" | "archived";
  readonly checksum: string;
}

// ============================================================================
// Default branding state
// ============================================================================

const DEFAULT_BRANDING: ThemeBrandingConfig = {
  version: 1,
  colors: {
    primary: "#2563EB",
    accent: "#7C3AED",
    background: "#FFFFFF",
    surface: "#F8FAFC",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
  },
  fonts: { heading: "Inter", body: "Inter" },
};

// ============================================================================
// Helpers
// ============================================================================

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  return envUrl ?? "http://localhost:8787";
}

// ============================================================================
// Color Section Component
// ============================================================================

interface ColorSectionProps {
  readonly title: string;
  readonly colors: BrandingColors;
  readonly onChange: (key: keyof BrandingColors, value: string) => void;
  readonly readOnly: boolean;
}

function ColorSection({ title, colors, onChange, readOnly }: ColorSectionProps): ReactElement {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Palette className="h-4 w-4 text-gray-500" />
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <ColorPicker
          label="Primary"
          value={colors.primary}
          onChange={(v) => onChange("primary", v)}
          readOnly={readOnly}
          required
        />
        <ColorPicker
          label="Accent"
          value={colors.accent}
          onChange={(v) => onChange("accent", v)}
          readOnly={readOnly}
          required
        />
        <ColorPicker
          label="Background"
          value={colors.background}
          onChange={(v) => onChange("background", v)}
          readOnly={readOnly}
          required
        />
        <ColorPicker
          label="Surface"
          value={colors.surface}
          onChange={(v) => onChange("surface", v)}
          readOnly={readOnly}
          required
        />
        <ColorPicker
          label="Text Primary"
          value={colors.textPrimary}
          onChange={(v) => onChange("textPrimary", v)}
          readOnly={readOnly}
          required
        />
        <ColorPicker
          label="Text Secondary"
          value={colors.textSecondary}
          onChange={(v) => onChange("textSecondary", v)}
          readOnly={readOnly}
          required
        />
      </div>

      {/* Optional status colors */}
      <div className="mt-4 border-t border-gray-100 pt-3">
        <p className="mb-2 text-xs font-medium text-gray-500">Status Colors (Optional)</p>
        <div className="grid grid-cols-2 gap-3">
          <ColorPicker
            label="Error"
            value={colors.error ?? "#EF4444"}
            onChange={(v) => onChange("error", v)}
            readOnly={readOnly}
          />
          <ColorPicker
            label="Success"
            value={colors.success ?? "#22C55E"}
            onChange={(v) => onChange("success", v)}
            readOnly={readOnly}
          />
          <ColorPicker
            label="Warning"
            value={colors.warning ?? "#F59E0B"}
            onChange={(v) => onChange("warning", v)}
            readOnly={readOnly}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function BrandingEditorPage(): ReactElement {
  const { verticalKey } = useParams<{ verticalKey: string }>();

  // State
  const [branding, setBranding] = useState<ThemeBrandingConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [originalJson, setOriginalJson] = useState<string>("");

  // File input ref for import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auth
  const hasWriteCapability = useAuthStore((s) => s.hasCapability("config:write"));
  const hasReadCapability = useAuthStore((s) => s.hasCapability("config:read"));

  // API setup
  const getAuthToken = useCallback((): string | null => {
    return useAuthStore.getState().token ?? null;
  }, []);
  const apiBaseUrl = getApiBaseUrl();

  const configApiClient = useMemo(
    () => createConfigApiClient({ baseUrl: apiBaseUrl, getAuthToken }),
    [apiBaseUrl, getAuthToken]
  );

  const lockApiClient = useMemo(
    () => createLockApiClient({ baseUrl: apiBaseUrl, getAuthToken }),
    [apiBaseUrl, getAuthToken]
  );

  // Ref for draft management
  const brandingRef = useRef<ThemeBrandingConfig>(branding);
  brandingRef.current = branding;

  const getContent = useCallback((): unknown => {
    return brandingRef.current;
  }, []);

  // Draft management
  const {
    saveStatus,
    markDirty,
    saveNow,
    saveError,
    canEdit,
    isReadOnly: lockReadOnly,
  } = useDraftManagement(
    configApiClient,
    lockApiClient,
    verticalKey ?? "",
    "branding",
    "branding.json",
    getContent,
    {
      intervalMs: 30000,
      autoAcquire: true,
      enabled: !!verticalKey,
    }
  );

  const isReadOnly = !hasWriteCapability || lockReadOnly;

  // Dirty tracking
  const isDirty = useMemo(() => {
    return JSON.stringify(branding) !== originalJson;
  }, [branding, originalJson]);

  // Load draft on mount
  useEffect(() => {
    if (!verticalKey) return;

    let cancelled = false;

    const loadDraft = async (): Promise<void> => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await apiRequest<ConfigContent>(
          `/api/v1/configs/${verticalKey}/branding/draft`
        );

        if (cancelled) return;

        if (response.success && response.data.content) {
          const parsed = JSON.parse(response.data.content) as ThemeBrandingConfig;
          setBranding(parsed);
          setOriginalJson(JSON.stringify(parsed));
        }
      } catch {
        if (!cancelled) {
          // Draft may not exist yet — use defaults
          setOriginalJson(JSON.stringify(DEFAULT_BRANDING));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadDraft();

    return (): void => {
      cancelled = true;
    };
  }, [verticalKey]);

  // Load live config
  const handleLoadLive = useCallback(async (): Promise<void> => {
    if (!verticalKey) return;

    try {
      const response = await apiRequest<ConfigContent>(`/api/v1/configs/${verticalKey}/branding`);

      if (response.success && response.data.content) {
        const parsed = JSON.parse(response.data.content) as ThemeBrandingConfig;
        setBranding(parsed);
        markDirty();
      }
    } catch {
      // Live config may not exist
    }
  }, [verticalKey, markDirty]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleColorChange = useCallback(
    (key: keyof BrandingColors, value: string): void => {
      setBranding((prev) => ({
        ...prev,
        colors: { ...prev.colors, [key]: value },
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleDarkColorChange = useCallback(
    (key: keyof BrandingColors, value: string): void => {
      setBranding((prev) => ({
        ...prev,
        darkColors: { ...(prev.darkColors ?? prev.colors), [key]: value },
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleFontChange = useCallback(
    (field: "heading" | "body", value: string): void => {
      setBranding((prev) => ({
        ...prev,
        fonts: { ...prev.fonts, [field]: value || undefined },
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleLogoUrlChange = useCallback(
    (value: string): void => {
      setBranding((prev) => ({
        ...prev,
        logoUrl: value || undefined,
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleFaviconUrlChange = useCallback(
    (value: string): void => {
      setBranding((prev) => ({
        ...prev,
        faviconUrl: value || undefined,
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleModeChange = useCallback(
    (mode: ThemeMode): void => {
      setBranding((prev) => ({
        ...prev,
        mode,
        // Initialize dark colors when switching to dark/system
        darkColors:
          (mode === "dark" || mode === "system") && !prev.darkColors
            ? { ...prev.colors }
            : prev.darkColors,
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleCustomCssChange = useCallback(
    (value: string): void => {
      // Sanitize: reject dangerous CSS patterns (XSS vectors)
      const dangerous =
        /@import\b|expression\s*\(|javascript\s*:|url\s*\(\s*['"]?\s*javascript\s*:/i;
      if (dangerous.test(value)) {
        return;
      }
      setBranding((prev) => ({
        ...prev,
        customCss: value || undefined,
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleVersionChange = useCallback(
    (value: number): void => {
      setBranding((prev) => ({
        ...prev,
        version: value,
      }));
      markDirty();
    },
    [markDirty]
  );

  const handlePresetApply = useCallback(
    (preset: BrandingPreset): void => {
      setBranding((prev) => ({
        ...prev,
        colors: { ...preset.colors },
        fonts: { ...preset.fonts },
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleSave = useCallback((): void => {
    if (canEdit) {
      markDirty();
      void saveNow();
      setOriginalJson(JSON.stringify(brandingRef.current));
    }
  }, [canEdit, markDirty, saveNow]);

  // Export JSON
  const handleExport = useCallback((): void => {
    // Strip ThemeBrandingConfig-specific fields for base export
    const exportData: BrandingConfig = {
      version: branding.version,
      colors: branding.colors,
      ...(branding.verticalKey ? { verticalKey: branding.verticalKey } : {}),
      ...(branding.logoUrl ? { logoUrl: branding.logoUrl } : {}),
      ...(branding.faviconUrl ? { faviconUrl: branding.faviconUrl } : {}),
      ...(branding.fonts ? { fonts: branding.fonts } : {}),
      ...(branding.customCss ? { customCss: branding.customCss } : {}),
    };
    exportBrandingJson(exportData);
  }, [branding]);

  // Import JSON
  const handleImportClick = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  const handleImportFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImportError(null);
      const result = await importBrandingJson(file);

      if (result.success) {
        setBranding({ ...DEFAULT_BRANDING, ...result.data });
        markDirty();
      } else {
        setImportError(result.error);
      }

      // Reset file input so same file can be imported again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [markDirty]
  );

  // Keyboard shortcut for save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (isDirty && canEdit) {
          handleSave();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDirty, canEdit, handleSave]);

  // ============================================================================
  // Render guards
  // ============================================================================

  if (!verticalKey) {
    return (
      <div className="flex h-full items-center justify-center text-foreground-muted">
        Invalid configuration path
      </div>
    );
  }

  if (!hasReadCapability) {
    return (
      <div className="flex h-full items-center justify-center text-foreground-muted">
        You do not have permission to view branding configuration.
      </div>
    );
  }

  const showDarkColors = branding.mode === "dark" || branding.mode === "system";

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-background-secondary px-4 py-2">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <Link
            to="/studio"
            className="flex items-center gap-1 text-foreground-muted hover:text-foreground"
          >
            <Home className="h-4 w-4" />
            <span>Studio</span>
          </Link>
          <ChevronRight className="h-4 w-4 text-foreground-muted" />
          <Link
            to={`/studio/${verticalKey}`}
            className="text-foreground-muted hover:text-foreground"
          >
            {verticalKey}
          </Link>
          <ChevronRight className="h-4 w-4 text-foreground-muted" />
          <span className="font-medium">Branding</span>
          {isDirty && (
            <span className="ml-2 inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
              Unsaved
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <BrandingPresetSelector onApply={handlePresetApply} disabled={isReadOnly} />

          <div className="mx-1 h-6 border-l border-gray-300" />

          <button
            onClick={handleLoadLive}
            className="btn btn-ghost text-sm"
            title="Load from live config"
            data-testid="load-live-button"
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Load Live
          </button>

          <button
            onClick={handleExport}
            className="btn btn-ghost text-sm"
            title="Export as JSON"
            data-testid="export-button"
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            Export
          </button>

          <button
            onClick={handleImportClick}
            disabled={isReadOnly}
            className="btn btn-ghost text-sm"
            title="Import from JSON"
            data-testid="import-button"
          >
            <Upload className="mr-1 h-3.5 w-3.5" />
            Import
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={(e) => void handleImportFile(e)}
            className="hidden"
            data-testid="import-file-input"
          />

          <div className="mx-1 h-6 border-l border-gray-300" />

          <button
            onClick={handleSave}
            disabled={!isDirty || !canEdit || saveStatus === "saving"}
            className="btn btn-primary"
            data-testid="save-button"
          >
            {saveStatus === "saving" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Draft
          </button>
        </div>
      </div>

      {/* Import error alert */}
      {importError && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          Import failed: {importError}
          <button
            onClick={() => setImportError(null)}
            className="ml-2 font-medium underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Save error alert */}
      {saveError && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          Save failed: {saveError}
        </div>
      )}

      {/* Main content */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
          <span className="ml-3 text-foreground-muted">Loading branding configuration...</span>
        </div>
      ) : loadError ? (
        <div className="flex flex-1 flex-col items-center justify-center">
          <p className="text-foreground-muted">{loadError}</p>
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Left panel - Controls */}
          <div
            className="flex-1 overflow-y-auto border-r border-gray-200 bg-gray-50 p-4"
            data-testid="controls-panel"
          >
            <div className="flex flex-col gap-4">
              {/* Version */}
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Version</label>
                <input
                  type="number"
                  value={branding.version}
                  onChange={(e) => handleVersionChange(parseInt(e.target.value, 10) || 1)}
                  readOnly={isReadOnly}
                  min={1}
                  className={`w-24 rounded-md border border-gray-300 px-3 py-2 text-sm ${
                    isReadOnly ? "cursor-not-allowed bg-gray-50" : ""
                  }`}
                />
              </div>

              {/* Theme Mode */}
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Theme Mode</h3>
                <div className="flex gap-2" data-testid="theme-mode-toggle">
                  {(["light", "dark", "system"] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => handleModeChange(mode)}
                      disabled={isReadOnly}
                      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                        branding.mode === mode || (!branding.mode && mode === "light")
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      } ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                      data-testid={`theme-mode-${mode}`}
                    >
                      {mode === "light" && <Sun className="h-3.5 w-3.5" />}
                      {mode === "dark" && <Moon className="h-3.5 w-3.5" />}
                      {mode === "system" && (
                        <>
                          <Sun className="h-3.5 w-3.5" />
                          <Moon className="h-3.5 w-3.5" />
                        </>
                      )}
                      <span className="capitalize">{mode}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Light Colors */}
              <ColorSection
                title="Colors"
                colors={branding.colors}
                onChange={handleColorChange}
                readOnly={isReadOnly}
              />

              {/* Dark Colors (conditional) */}
              {showDarkColors && (
                <ColorSection
                  title="Dark Mode Colors"
                  colors={branding.darkColors ?? branding.colors}
                  onChange={handleDarkColorChange}
                  readOnly={isReadOnly}
                />
              )}

              {/* Typography */}
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Typography</h3>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Heading Font
                    </label>
                    <input
                      type="text"
                      value={branding.fonts?.heading ?? ""}
                      onChange={(e) => handleFontChange("heading", e.target.value)}
                      readOnly={isReadOnly}
                      placeholder="Inter, system-ui, sans-serif"
                      className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm ${
                        isReadOnly ? "cursor-not-allowed bg-gray-50" : ""
                      }`}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Body Font
                    </label>
                    <input
                      type="text"
                      value={branding.fonts?.body ?? ""}
                      onChange={(e) => handleFontChange("body", e.target.value)}
                      readOnly={isReadOnly}
                      placeholder="Inter, system-ui, sans-serif"
                      className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm ${
                        isReadOnly ? "cursor-not-allowed bg-gray-50" : ""
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Logo & Favicon */}
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Logo & Favicon</h3>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Logo URL</label>
                    <input
                      type="url"
                      value={branding.logoUrl ?? ""}
                      onChange={(e) => handleLogoUrlChange(e.target.value)}
                      readOnly={isReadOnly}
                      placeholder="https://example.com/logo.png"
                      className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm ${
                        isReadOnly ? "cursor-not-allowed bg-gray-50" : ""
                      }`}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Favicon URL
                    </label>
                    <input
                      type="url"
                      value={branding.faviconUrl ?? ""}
                      onChange={(e) => handleFaviconUrlChange(e.target.value)}
                      readOnly={isReadOnly}
                      placeholder="https://example.com/favicon.ico"
                      className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm ${
                        isReadOnly ? "cursor-not-allowed bg-gray-50" : ""
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Custom CSS */}
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Custom CSS</h3>
                <textarea
                  value={branding.customCss ?? ""}
                  onChange={(e) => handleCustomCssChange(e.target.value)}
                  readOnly={isReadOnly}
                  placeholder="/* Add custom CSS here */"
                  rows={6}
                  maxLength={50000}
                  className={`w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm ${
                    isReadOnly ? "cursor-not-allowed bg-gray-50" : ""
                  }`}
                  data-testid="custom-css-textarea"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Maximum 50,000 characters. CSS will be scoped to your vertical.
                </p>
              </div>
            </div>
          </div>

          {/* Right panel - Live Preview */}
          <div
            className="flex w-[420px] flex-shrink-0 flex-col overflow-y-auto bg-gray-100 p-4"
            data-testid="preview-panel"
          >
            <h3 className="mb-3 text-sm font-semibold text-gray-700">Live Preview</h3>
            <LivePreviewCard
              colors={branding.colors}
              fonts={branding.fonts}
              logoUrl={branding.logoUrl}
            />

            {/* Dark mode preview */}
            {showDarkColors && branding.darkColors && (
              <>
                <h3 className="mb-3 mt-6 text-sm font-semibold text-gray-700">Dark Mode Preview</h3>
                <LivePreviewCard
                  colors={branding.darkColors}
                  fonts={branding.fonts}
                  logoUrl={branding.logoUrl}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Status bar */}
      <div className="flex items-center justify-between border-t border-border bg-background-secondary px-4 py-1 text-xs text-foreground-muted">
        <div className="flex items-center gap-4">
          <span>Branding</span>
          <span>Version {branding.version}</span>
        </div>
        <div className="flex items-center gap-4">
          {saveStatus === "saving" && <span>Saving...</span>}
          {saveStatus === "saved" && <span>Saved</span>}
          {saveStatus === "error" && <span className="text-red-500">Save error</span>}
          {isDirty && <span className="text-amber-500">Modified</span>}
        </div>
      </div>
    </div>
  );
}
